import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
    AngularFirestore,
    DocumentData,
    DocumentReference,
} from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    finalize,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import { RoleModel } from '../../../app/shared/models/role.model';
import {
    COMPANY_LEADS_COLLECTION,
    DEFAULT_LANGUAGE,
    EMPLOYEES_COLLECTION,
    STATUS_ACTIVE,
    STATUS_DATA_EXPORT_PENDING,
    STATUS_INACTIVE,
    STATUS_PENDING_OPTIN,
    USER_LOCAL_STORAGE_KEY,
    USERS_COLLECTION
} from '../../app.constants';
import { FinsteinHelperTypesEnum } from '../../shared/enums/finstein-helper-types.enum';
import { LoggedInUser } from '../../shared/models/user.model';
import { BaseService } from './base.service';
import { FireLoggingService } from './fire-logging.service';
import { LanguageService } from './language.service';
import { StorageService } from './storage.service';

export interface IBrowserClientData {
    ip: string;
    createdAt: Date;
    userAgent: string;
}

const getChildValues = async (reference: DocumentReference<DocumentData>) => {
    const value = (await reference.get()).data();
    if (value) {
        delete value['createdBy'];
        return { ...value, id: value['id'] };
    }
    return null;
};

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService {
    public browserClientData: IBrowserClientData;
    private listenerChangesSubscription: any;
    private loggedInUser = new LoggedInUser();
    operatedByHelperUser = false;

    constructor(
        protected override firestore: AngularFirestore,
        protected override http: HttpClient,
        protected override functions: AngularFireFunctions,
        protected override fireLogging: FireLoggingService,
        private storageService: StorageService,
        private languageService: LanguageService,
        public afAuth: AngularFireAuth,
        private firebaseDb: AngularFireDatabase,
        private router: Router
    ) {
        super(firestore, http, functions, fireLogging);
    }

    get loggedUser(): any {
        return this.loggedInUser;
    }

    set loggedUser(user) {
        this.loggedInUser = user;
    }

    getLoggedInUser() {
        return this.loggedInUser;
    }

    destroyAllUserObservables() {
        if (this.listenerChangesSubscription) {
            this.listenerChangesSubscription.unsubscribe();
        }
    }

    isFinsteinHelper() {
        return this.loggedUser?.finsteinHelper &&
         [
            FinsteinHelperTypesEnum.COP_HELPER,
            FinsteinHelperTypesEnum.GLOBAL_HELPER,
            FinsteinHelperTypesEnum.BROKER_HELPER
        ].includes(this.loggedUser.finsteinHelperType);
    }

    updateLanguage(newLang: string) {
        return new Promise((resolve, reject) => {
            if (!!this.loggedUser && !!this.loggedUser.id) {
                this.firestore
                    .collection(this.getUserCollectionByRole())
                    .doc(this.loggedUser.id)
                    .set(
                        {
                            lang: newLang,
                        },
                        { merge: true }
                    )
                    .then(() => resolve(null))
                    .catch((e) => reject(e))
                    .catch((error) => {
                        const message = error.error
                            ? error.error.message
                            : error.message;
                        this.fireLogging
                            .sendErrorLog(`[updateLanguage(user.service.ts)] An error occurred
                        while updating item into collection ${this.getUserCollectionByRole()}/${
                            this.loggedUser.id
                        },
                        details: ${message || JSON.stringify(error)}`);
                    });
            } else {
                reject('User not logged in.');
            }
        });
    }

    getUserCollectionByRole() {
        if (
            this.loggedUser.roles.find(
                (item: any) => item.employeeRole === true
            )
        ) {
            return EMPLOYEES_COLLECTION;
        } else if (
            this.loggedUser.roles.find((item: any) => item.faqRole === true)
        ) {
            return COMPANY_LEADS_COLLECTION;
        }
        return '';
    }

    getLoggedUserPermissions() {
        const permissions: any[] = [];
        for (const role of this.loggedUser.roles) {
            for (const perm of role.permissions) {
                if (!permissions.includes(perm)) {
                    permissions.push(perm);
                }
            }
        }
        return permissions;
    }

    getStoredLoggedUser() {
        return this.storageService.get(USER_LOCAL_STORAGE_KEY);
    }

    isLogged(): Observable<boolean> {
        return this.afAuth.idTokenResult.pipe(
            map((res: any) => {
                const condition =
                    !!res &&
                    res.claims &&
                    res.claims.email &&
                    this.loggedUser &&
                    this.loggedUser.email &&
                    this.isNotExpired(res);

                if (this.operatedByHelperUser) {
                    return condition;
                } else {
                    return (
                        condition &&
                        res.claims.email.toLowerCase() ===
                            this.loggedUser.email.toLowerCase()
                    );
                }
            })
        );
    }

    hasAnyAuthority(permissions: string[] | string): boolean {
        if (!this.isLogged() || !this.loggedUser || !this.loggedUser.roles) {
            return false;
        }
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }

        const userPermissions: any[] = [];
        this.loggedUser.roles.forEach((role: RoleModel) => {
            role.permissions?.forEach((permission: any) => {
                userPermissions.push(permission);
            });
        });
        return permissions.some((authority: string) =>
            userPermissions.includes(authority)
        );
    }

    /**
     * Listen for changes in the logged-in user.
     */
    listenForChangesAtUser(
        collection: string,
        userId?: string
    ): Observable<LoggedInUser | any> {
        const id = userId || this.loggedUser.id;
        return this.firestore
            .collection(collection)
            .doc(id)
            .snapshotChanges()
            .pipe(
                take(1),
                map((data) => {
                    const obj: any = data.payload.data();
                    if (
                        collection === USERS_COLLECTION &&
                        !obj.finsteinHelper
                    ) {
                        throw new Error('The user is not a helper user.');
                    }
                    return {
                        id: data.payload.id,
                        ref: data.payload.ref,
                        ...obj,
                    };
                }),
                concatMap(this.buildUserObj),
                tap((data) => {
                    this.loggedUser = data;
                    if (this.loggedInUser.finsteinHelper) {
                        this.operatedByHelperUser = true;
                    }
                }),
                finalize(() => {
                    this.storageService.save(
                        USER_LOCAL_STORAGE_KEY,
                        this.loggedUser
                    );
                    this.languageService.changeLanguage(
                        this.loggedUser.lang
                            ? this.loggedUser.lang
                            : DEFAULT_LANGUAGE
                    );
                    this.languageService.languageChangeEmitter.emit();
                    return this.loggedUser;
                }),
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`[listenForChangesAtUser(user.service.ts)] An error occurred
                     while getting item into collection ${collection}/${id}, details: ${
                        message || JSON.stringify(error)
                    }`);
                    return of({ error });
                })
            );
    }

    listenForEmployeeStatusAndSignOut() {
        const id = this.loggedUser.id;
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(id)
            .snapshotChanges()
            .pipe(
                switchMap((data) => {
                    const obj: any = data.payload.data();
                    if (
                        [
                            STATUS_ACTIVE,
                            STATUS_INACTIVE,
                            STATUS_DATA_EXPORT_PENDING,
                            STATUS_PENDING_OPTIN,
                        ].includes(obj.status)
                    ) {
                        return from(this.afAuth.signOut()).pipe(
                            take(1),
                            tap(() => {
                                if (this.loggedUser) {
                                    const path = `website/user-sessions/${this.loggedUser.id}`;
                                    this.firebaseDb.database
                                        .ref(path)
                                        .off('value');
                                }
                                this.destroyAllUserObservables();
                                this.storageService.remove(
                                    USER_LOCAL_STORAGE_KEY
                                );
                                this.loggedUser = new LoggedInUser();
                                this.router.navigate(['/auth']);
                            })
                        );
                    }
                    return EMPTY;
                })
            );
    }

    /**
     * Map the reference of sub-collections to the user (sub-collections: roles, subscription list...)
     * @param userData The user retrieved
     */
    async buildUserObj(userData: DocumentData) {
        const user = new LoggedInUser();
        user.id = userData['id'];
        user.firstName = userData['firstName'];
        user.email = userData['email'] || userData['companyEmail'];
        user.lastName = userData['lastName'];
        user.lang = userData['lang'];
        user.finsteinHelper = userData['finsteinHelper'];
        user.finsteinHelperType = userData['finsteinHelperType'];
        user.companyId = userData['companyId'];
        user.birthday = userData['birthday'];
        user.status = userData['status'];
        user.companyId = userData['companyId'];
        user.emailVerified = userData['emailVerified'];
        user.copExpired = userData['copExpired'];
        user.phone = userData['phone'];
        user.lastConsultancyProcessRef = userData['lastConsultancyProcessRef'];
        user.consultancyProcessAlreadyFinished =
            !!userData['lastConsultancyProcessFinishedDate'];
        if (userData['roles']) {
            await Promise.all(userData['roles'].map(getChildValues)).then(
                (roleList) => (user.roles = roleList)
            );
        }
        return user;
    }

    private isNotExpired({ expirationTime }: any) {
        return new Date(expirationTime) > new Date();
    }
}
